/**
 * ReMixChart - Optimized for Large Datasets
 * Split entry. See ./index.ts for public exports.
 */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import {
  BrushStart,
  ChartArguements,
  EditableSeriesConfig,
  EditableSeriesPointChange,
  LegendKey,
  ReChartData,
} from "../../../models/chart_model";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { compareLegends, legendsEqual } from "./utils/legendUtils";
import { LegendItem } from "./components/LegendComponent";
import { useBuildSeries } from "./hooks/useChartSeries";
import { useTooltipFormatter } from "./hooks/useTooltipFormatter";
import { useChartOptions } from "./hooks/useChartOptions";
import { useZoomState } from "./hooks/useZoomState";
import { useLegendDerivations } from "./hooks/useColorMap";
import { useEmptyStateVisibility } from "./hooks/useEmptyStateVisibility";
import { ChartClickParams } from "./types";
import "./ReMixChart.css";
import { EmptyState } from "./components/EmptyState";

export function ReMixChart({
  data,
  legends,
  onLegendSelect,
  unit = "",
  xDataKey,
  xLabel,
  yAxisLabel,
  legendFontSize,
  secondYAxisLabel,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  disableLegendClick = false,
  secondXDataKey,
  showBrush = true,
  brushIndex,
  labelFontSize,
  brushHeight = 17,
  brushStart = BrushStart.End,
  xaxisHeight = 35,
  barCategoryGap = 2.5,
  xAxisTickMargin = 8,
  isTimeSlot,
  xAxisAngle,
  onBarClick,
  fontSize = 12,
  customTooltip,
  hiddenLegends = [],
  hiddenStats = [],
  enableMouseWheelZoom = true,
  chartContainerHeight = "",
  nameGap,
  showToolbox = false,
  yMaxValue,
  tooltipFilter,
  legendMaxHeight,
  isPriceForecast = false,
  defaultSelectedLegends = [],
  top = 30,
  bottom = 20,
  left,
  secondYAxisDomain,
  isSmallLegend = false,
  customLegendOrder,
  xAxisInterval,
  colorByPoint,
  barColors,
  highlightedXValues,
  enableClickSelection,
  editableSeries,
  isLoading,
  emptyStateDelayMs = 450,
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>(
    defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends,
  );

  const dispatch = useDispatch();
  const chartRef = useRef<ReactECharts>(null);
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef<ReChartData[]>(data);
  const editableSeriesRef = useRef<EditableSeriesConfig | undefined>(
    editableSeries,
  );
  const xDataKeyRef = useRef(xDataKey);
  const selectedLegendsRef = useRef<LegendKey[]>(selectedLegends);
  const editableFrameRef = useRef<number | null>(null);
  const syncEditableGraphicsRef = useRef<() => void>(() => {});
  const editableDragRef = useRef<{
    dataIndex: number;
    value: number;
  } | null>(null);
  const dragTooltipStateRef = useRef<{
    active: boolean;
    dataIndex?: number;
    seriesName?: string;
    accentColor?: string;
  }>({ active: false });
  const [isCompact, setIsCompact] = useState(false);
  const prevLegendsRef = useRef<LegendKey[]>([]);
  const prevDefaultLegendsRef = useRef<LegendKey[]>([]);

  const isEmpty = !data || data.length === 0;
  const showEmpty = useEmptyStateVisibility({
    isEmpty,
    isLoading,
    delayMs: emptyStateDelayMs,
  });

  useEffect(() => {
    if (!isEmpty) return;
    const el = chartWrapRef.current;
    if (!el) return;
    const update = () => setIsCompact(el.clientHeight < 160);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isEmpty]);

  const { lastZoom, zoomRange, handleDataZoom, consumeSkipFlag } = useZoomState(
    data,
    brushIndex?.startIndex,
    brushIndex?.endIndex,
    xDataKey,
  );

  // hideTip on unmount
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    return () => {
      if (chartInstance) {
        chartInstance.dispatchAction({ type: "hideTip" });
      }
    };
  }, []);

  useEffect(() => {
    const newLegends =
      defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends;
    const prevDefaults = prevDefaultLegendsRef.current;
    const prevLeg = prevLegendsRef.current;
    if (!legendsEqual(newLegends, selectedLegends)) {
      if (
        !legendsEqual(defaultSelectedLegends, prevDefaults) ||
        !legendsEqual(legends, prevLeg)
      ) {
        setSelectedLegends(newLegends);
      }
    }
    prevDefaultLegendsRef.current = defaultSelectedLegends;
    prevLegendsRef.current = legends;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedLegends, legends]);

  const debouncedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  useEffect(() => {
    dataRef.current = debouncedData;
  }, [debouncedData]);

  useEffect(() => {
    editableSeriesRef.current = editableSeries;
  }, [editableSeries]);

  useEffect(() => {
    xDataKeyRef.current = xDataKey;
  }, [xDataKey]);

  useEffect(() => {
    selectedLegendsRef.current = selectedLegends;
  }, [selectedLegends]);

  const buildSeries = useBuildSeries();
  const getDragTooltipState = useCallback(() => dragTooltipStateRef.current, []);

  const handleLegendClick = useCallback(
    (legend: LegendKey) => {
      if (disableLegendClick) return;
      setSelectedLegends((prevSelectedLegends) => {
        const allSelected =
          prevSelectedLegends.length === legends.length ||
          prevSelectedLegends.length === 0;
        let newSelectedLegends;
        if (allSelected) {
          newSelectedLegends = [legend];
        } else {
          const exists = prevSelectedLegends.some(
            (item) => item.dataKey === legend.dataKey,
          );
          newSelectedLegends = exists
            ? prevSelectedLegends.filter(
                (item) => item.dataKey !== legend.dataKey,
              )
            : [...prevSelectedLegends, legend];
        }
        if (onLegendSelect) onLegendSelect(newSelectedLegends);
        return newSelectedLegends;
      });
    },
    [disableLegendClick, onLegendSelect, legends],
  );

  const { filteredLegends, colorMap, orderedSelectedLegends } =
    useLegendDerivations(legends, selectedLegends, xDataKey, customLegendOrder);

  const xAxisData = useMemo(
    () => debouncedData?.map((item) => item[xDataKey]) || [],
    [debouncedData, xDataKey],
  );

  const removeEditableGraphics = useCallback((chartInstance?: any) => {
    const instance =
      chartInstance ?? chartRef.current?.getEchartsInstance?.();
    if (!instance || instance.isDisposed?.()) {
      return;
    }
    instance.setOption(
      { graphic: [] },
      { replaceMerge: ["graphic"], lazyUpdate: true } as any,
    );
  }, []);

  const roundEditableValue = useCallback(
    (rawValue: number, config: EditableSeriesConfig) => {
      let nextValue = rawValue;
      if (typeof config.min === "number") {
        nextValue = Math.max(config.min, nextValue);
      }
      if (typeof config.max === "number") {
        nextValue = Math.min(config.max, nextValue);
      }
      if (typeof config.step === "number" && config.step > 0) {
        nextValue = Math.round(nextValue / config.step) * config.step;
      }
      const decimals =
        typeof config.step === "number" && String(config.step).includes(".")
          ? String(config.step).split(".")[1].length
          : 3;
      return Number(nextValue.toFixed(Math.min(decimals + 2, 8)));
    },
    [],
  );

  const buildEditablePayload = useCallback(
    (
      dataIndex: number,
      newValue: number,
      config: EditableSeriesConfig,
    ): EditableSeriesPointChange | null => {
      const rows = dataRef.current;
      const row = rows[dataIndex];
      if (!row) return null;
      const oldValue = Number(row[config.dataKey]);
      if (!Number.isFinite(oldValue)) return null;
      return {
        dataIndex,
        xValue: row[xDataKeyRef.current],
        oldValue,
        newValue,
        row,
      };
    },
    [],
  );

  const syncEditableGraphics = useCallback(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    const config = editableSeriesRef.current;
    const rows = dataRef.current;
    if (editableDragRef.current) return;
    if (
      !chartInstance ||
      chartInstance.isDisposed?.() ||
      !config?.enabled ||
      !config.dataKey ||
      rows.length === 0
    ) {
      removeEditableGraphics(chartInstance);
      return;
    }

    const selected = selectedLegendsRef.current;
    const isSeriesVisible =
      selected.length === 0 ||
      selected.some((legend) => legend.dataKey === config.dataKey);
    if (!isSeriesVisible) {
      removeEditableGraphics(chartInstance);
      return;
    }

    const option = chartInstance.getOption?.();
    const series = Array.isArray(option?.series) ? option.series : [];
    const xAxes = Array.isArray(option?.xAxis) ? option.xAxis : [];
    const yAxes = Array.isArray(option?.yAxis) ? option.yAxis : [];
    const hasEditableSeries = series.some(
      (item: any) => item.id === config.dataKey || item.name === config.dataKey,
    );
    if (xAxes.length === 0 || yAxes.length === 0 || !hasEditableSeries) return;

    const yAxisIndex = config.yAxisId === "right" ? 1 : 0;
    const graphics = rows
      .map((row, dataIndex) => {
        const value = Number(row[config.dataKey]);
        if (!Number.isFinite(value)) return null;

        const pixel = chartInstance.convertToPixel(
          { xAxisIndex: 0, yAxisIndex },
          [dataIndex, value],
        );
        if (
          !Array.isArray(pixel) ||
          !Number.isFinite(pixel[0]) ||
          !Number.isFinite(pixel[1])
        ) {
          return null;
        }

        const handleSize = config.handleSize ?? 4.5;

        return {
          type: "circle",
          x: pixel[0],
          y: pixel[1],
          shape: { r: handleSize },
          draggable: true,
          cursor: "ns-resize",
          z: 1000,
          style: {
            fill: config.handleColor ?? "#f1935c",
            stroke: "#ffffff",
            lineWidth: 1.5,
            shadowBlur: 2,
            shadowColor: "rgba(0, 0, 0, 0.14)",
            opacity: 0.95,
          },
          onmousemove: () => {
            chartInstance.dispatchAction({
              type: "showTip",
              seriesId: config.dataKey,
              dataIndex,
            });
          },
          onmouseout: () => {
            chartInstance.dispatchAction({ type: "hideTip" });
          },
          ondrag: function (this: any) {
            const liveConfig = editableSeriesRef.current;
            if (!liveConfig?.enabled) return;
            const currentRows = dataRef.current;
            const currentRow = currentRows[dataIndex];
            if (!currentRow) return;

            const currentValue = Number(currentRow[liveConfig.dataKey]);
            if (!Number.isFinite(currentValue)) return;
            const currentPixel = chartInstance.convertToPixel(
              { xAxisIndex: 0, yAxisIndex },
              [dataIndex, currentValue],
            );
            const converted = chartInstance.convertFromPixel(
              { xAxisIndex: 0, yAxisIndex },
              [currentPixel[0], this.y],
            );
            const rawNextValue = Number(converted?.[1]);
            if (!Number.isFinite(rawNextValue)) return;

            const newValue = roundEditableValue(rawNextValue, liveConfig);
            const snappedPixel = chartInstance.convertToPixel(
              { xAxisIndex: 0, yAxisIndex },
              [dataIndex, newValue],
            );
            this.x = currentPixel[0];
            if (
              Array.isArray(snappedPixel) &&
              Number.isFinite(snappedPixel[1])
            ) {
              this.y = snappedPixel[1];
            }

            if (Math.abs(newValue - currentValue) < 0.000001) return;

            editableDragRef.current = { dataIndex, value: newValue };
            const previewData = currentRows.map((previewRow, previewIndex) =>
              previewIndex === dataIndex
                ? newValue
                : (previewRow[liveConfig.dataKey] ?? undefined),
            );
            chartInstance.setOption({
              series: [{ id: liveConfig.dataKey, data: previewData }],
            });
            chartInstance.dispatchAction({
              type: "showTip",
              seriesId: liveConfig.dataKey,
              dataIndex,
              position: [currentPixel[0], this.y],
            });
          },
          ondragend: function () {
            const liveConfig = editableSeriesRef.current;
            if (!liveConfig?.enabled) return;
            const dragged = editableDragRef.current;
            editableDragRef.current = null;
            if (!dragged || dragged.dataIndex !== dataIndex) {
              requestAnimationFrame(() => syncEditableGraphicsRef.current());
              return;
            }
            const currentRows = dataRef.current;
            const currentRow = currentRows[dataIndex];
            if (!currentRow) return;
            const payload = buildEditablePayload(
              dataIndex,
              dragged.value,
              liveConfig,
            );
            if (payload) {
              liveConfig.onPointChange(payload);
              liveConfig.onPointChangeEnd?.(payload);
            }
            requestAnimationFrame(() => syncEditableGraphicsRef.current());
          },
        };
      })
      .filter(Boolean);

    chartInstance.setOption(
      { graphic: graphics },
      { replaceMerge: ["graphic"], lazyUpdate: true } as any,
    );
  }, [
    buildEditablePayload,
    removeEditableGraphics,
    roundEditableValue,
  ]);

  useEffect(() => {
    syncEditableGraphicsRef.current = syncEditableGraphics;
  }, [syncEditableGraphics]);

  const scheduleEditableGraphicSync = useCallback(() => {
    if (editableFrameRef.current !== null) {
      cancelAnimationFrame(editableFrameRef.current);
    }
    editableFrameRef.current = requestAnimationFrame(() => {
      editableFrameRef.current = null;
      syncEditableGraphics();
    });
  }, [syncEditableGraphics]);

  useEffect(() => {
    if (editableSeries?.enabled) return;
    editableDragRef.current = null;
    removeEditableGraphics();
  }, [editableSeries?.enabled, editableSeries?.dataKey, removeEditableGraphics]);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el || !editableSeries?.enabled) return;
    const ro = new ResizeObserver(scheduleEditableGraphicSync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editableSeries?.enabled, scheduleEditableGraphicSync]);

  useEffect(
    () => () => {
      if (editableFrameRef.current !== null) {
        cancelAnimationFrame(editableFrameRef.current);
      }
      removeEditableGraphics();
    },
    [removeEditableGraphics],
  );

  const tooltipFormatter = useTooltipFormatter({
    tooltipFilter,
    secondXDataKey,
    debouncedData,
    isTimeSlot,
    fontSize,
    isPriceForecast,
    legends,
    hidePrice,
    unit,
    customLegendOrder,
    getDragTooltipState,
  });

  const chartOptions = useChartOptions({
    debouncedData,
    orderedSelectedLegends,
    selectedLegends,
    hiddenStats,
    colorMap,
    barCategoryGap,
    isPriceForecast,
    brushStart,
    left,
    showBrush: true,
    brushHeight,
    xaxisHeight,
    isSmallLegend,
    bottom,
    top,
    customTooltip,
    tooltipFormatter,
    fontSize,
    showToolbox,
    xAxisData,
    xAxisAngle,
    xAxisTickMargin,
    xLabel,
    xAxisLabelPosition,
    xAxisLabelOffset,
    labelFontSize,
    secondXDataKey,
    yMaxValue,
    yAxisLabel,
    nameGap,
    secondYAxisLabel,
    brushIndex,
    enableMouseWheelZoom,
    secondYAxisDomain,
    buildSeries,
    colorByPoint,
    barColors,
    enableClickSelection,
    highlightedXValues,
    xDataKey,
    xAxisInterval,
    isTimeSlot,
    zoomRange,
    lastZoomRef: lastZoom,
  });

  const onChartEvents = useMemo(
    () => ({
      click: (params: ChartClickParams) => {
        if (params.seriesName?.startsWith("__")) {
          if (onBarClick) {
            const clickedData = debouncedData[params.dataIndex ?? -1];
            if (clickedData) {
              onBarClick({
                ...params,
                name: clickedData[xDataKey],
                seriesName: "user_click",
                data: clickedData,
              });
            }
          }
        } else if (onBarClick) {
          onBarClick(params);
        }
      },
      datazoom: (params: any) => {
        if (consumeSkipFlag()) return;
        let start: number | undefined;
        let end: number | undefined;
        const zoom = params.batch ? params.batch[0] : params;
        if (zoom.start !== undefined && zoom.end !== undefined) {
          start = zoom.start;
          end = zoom.end;
        } else if (chartRef.current) {
          const chartInstance = chartRef.current.getEchartsInstance();
          const option = chartInstance.getOption();
          const dz = option.dataZoom as any[];
          if (dz && dz.length > 0) {
            start = dz[0].start;
            end = dz[0].end;
          }
        }
        if (start !== undefined && end !== undefined) {
          handleDataZoom(start, end);
          scheduleEditableGraphicSync();
        }
      },
      finished: () => {
        requestAnimationFrame(() => dispatch(setLoadingFilterName("")));
        scheduleEditableGraphicSync();
      },
    }),
    [
      onBarClick,
      debouncedData,
      xDataKey,
      dispatch,
      consumeSkipFlag,
      handleDataZoom,
      scheduleEditableGraphicSync,
    ],
  );

  const containerStyle = useMemo(
    () =>
      ({
        height: chartContainerHeight,
        ["--remix-fontSize" as any]: `${fontSize}px`,
        ["--remix-legendFontSize" as any]: `${legendFontSize ?? fontSize}px`,
      }) as React.CSSProperties,
    [chartContainerHeight, fontSize, legendFontSize],
  );

  return (
    <>
      <div
        style={containerStyle}
        className="chart-container tab-animatable-content remix-tooltip-vars"
      >
        <div
          ref={chartWrapRef}
          className="remix-chart-wrap group relative h-full w-full overflow-hidden"
          data-compact={isCompact ? "true" : "false"}
        >
          <ReactECharts
            ref={chartRef}
            option={chartOptions}
            style={{
              height: "100%",
              width: "100%",
              visibility: isEmpty && showEmpty ? "hidden" : "visible",
            }}
            opts={{ renderer: "canvas" }}
            lazyUpdate={true}
            onEvents={onChartEvents}
            notMerge={true}
          />
          {isEmpty && showEmpty && <EmptyState compact={isCompact} />}
        </div>
      </div>

      <div
        style={{
          maxHeight: legendMaxHeight || "17%",
          display: isEmpty ? "none" : undefined,
        }}
        className={`flex flex-wrap justify-center gap-x-2 overflow-legend tab-animatable-content remix-tooltip-vars ${
          showBrush && !isSmallLegend && "pt-1"
        }`}
      >
        {filteredLegends
          .slice()
          .sort((a, b) =>
            compareLegends(a.name || "", b.name || "", customLegendOrder),
          )
          .map((legend, index) => {
            const isHidden = hiddenLegends.includes(legend.name ?? "");
            return (
              <React.Fragment key={index}>
                <div className={`mb-3 ${isHidden ? "hidden" : ""}`}>
                  <LegendItem
                    legend={legend}
                    index={index}
                    selectedLegends={selectedLegends}
                    handleLegendClick={handleLegendClick}
                    circleOnly={circleLegend}
                    fontSize={legendFontSize}
                    colorMap={colorMap}
                    isSmallLegend={isSmallLegend}
                  />
                </div>
                {legendBreakIndex != 0 && legendBreakIndex === index && (
                  <div style={{ width: "100%", height: "2px" }}></div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </>
  );
}
